import React, { FocusEventHandler, SyntheticEvent, useMemo } from 'react';
import type {
  BasePatcherProps,
  WithPassedPatchableGroupProps
} from './PatchableGroup';
import {
  Autocomplete,
  AutocompleteProps,
  TextField,
  TextFieldProps
} from '@mui/material';
import { passthrough } from '@/util';

type ExcludedProps =
  | 'value'
  | 'onChange'
  | 'renderInput'
  | 'options'
  | 'multiple'
  | keyof BasePatcherProps;

export type AutocompletePatcherProps<T> = BasePatcherProps &
  Omit<AutocompleteProps<T, any, true, true>, ExcludedProps> & {
    renderInput?: AutocompleteProps<T, true, true, true>['renderInput'];
    label?: TextFieldProps['label'];
    options?: T[];
    textFieldProps?: TextFieldProps;
    transformValue?: (val?: string) => string | undefined;
    multiple?: boolean;
  };

const AutocompletePatcherComponent = <T,>({
  /* eslint-disable @typescript-eslint/no-unused-vars */
  name,
  error,
  wrapper,
  patchable,
  loading,
  label,
  options = [],
  hideLoadingOverlay,
  textFieldProps,
  sourceType,
  transformValue = passthrough,
  multiple = true,
  /* eslint-enable @typescript-eslint/no-unused-vars */
  ...restProps
}: WithPassedPatchableGroupProps<T, AutocompletePatcherProps<T>>) => {
  if (!patchable) {
    throw new Error(
      'A Patcher component may be used as a child of a PatchableGroup'
    );
  }

  const onValueChange = (e: SyntheticEvent, val: string | (T | string)[]) => {
    // TODO extract value from T if T is not a string and handle user input
    patchable.onPatch({ [name]: val });
  };

  const onBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (
    e
  ) => {
    textFieldProps?.onBlur?.(e);
    if (patchable.readOnly) {
      return;
    }
    if (multiple) {
      if (e.target.value) {
        const val = (patchable?.currentValue ?? []).concat(e.target.value);
        patchable.onPatch({ [name]: val });
      }
    } else {
      patchable.onPatch({ [name]: e.target.value ?? null });
    }
  };

  const currentValue = useMemo(() => {
    if (multiple) {
      return (patchable?.currentValue ?? []).map(transformValue);
    }

    return transformValue(patchable?.currentValue) ?? '';
  }, [multiple, transformValue, patchable?.currentValue]);

  return (
    <Autocomplete
      freeSolo
      multiple={multiple}
      fullWidth
      filterSelectedOptions
      selectOnFocus
      clearOnBlur
      disableClearable
      size="small"
      readOnly={patchable.readOnly}
      options={options}
      loading={loading}
      value={currentValue}
      onChange={onValueChange as any}
      ChipProps={{
        color: 'riskNone'
      }}
      renderInput={(p) => (
        <TextField
          label={label}
          color="riskNone"
          {...textFieldProps}
          error={!!error}
          helperText={error}
          onBlur={onBlur}
          {...p}
        />
      )}
      {...restProps}
    />
  );
};

export const AutocompletePatcher = <T,>(props: AutocompletePatcherProps<T>) => (
  <AutocompletePatcherComponent {...props} />
);
